import { DestroyRef, Directive, ElementRef, afterRenderEffect, inject, input } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

/** Long enough that sweeping the pointer down a list does not trail tooltips behind it. */
const SHOW_DELAY_MS = 150;

/**
 * Shows the host's own text as a tooltip, but only while an ellipsis is hiding part of it, so the
 * tooltip never just repeats what is already legible on screen.
 *
 * Whether the text fits is measured after each render rather than when the pointer arrives:
 * Material opens a tooltip on keyboard focus through `FocusMonitor`, which listens on the document
 * in the capture phase, so a handler on the element itself would decide only after the tooltip had
 * already been asked to show.
 *
 * `aria-label` carries the same text so Material leaves `aria-describedby` off. Clipping is visual
 * only — the full text is in the DOM and already in the accessible name — so a description would
 * have a screen reader read the name twice.
 */
@Directive({
  selector: '[appTooltipWhenTruncated]',
  hostDirectives: [
    {
      directive: MatTooltip,
      inputs: ['matTooltipPosition', 'matTooltipClass'],
    },
  ],
  host: {
    '[attr.aria-label]': 'text() || null',
  },
})
export class TooltipWhenTruncatedDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly tooltip = inject(MatTooltip);
  private readonly destroyRef = inject(DestroyRef);

  readonly text = input.required<string>({ alias: 'appTooltipWhenTruncated' });

  constructor() {
    this.tooltip.showDelay = SHOW_DELAY_MS;

    afterRenderEffect(() => {
      this.tooltip.message = this.text();
      this.syncTruncation();
    });

    // The same text is clipped or not depending on the room it is given, and the host can be
    // resized without re-rendering — a narrowed window, a longer sibling in the same row.
    const observer = new ResizeObserver(() => this.syncTruncation());
    observer.observe(this.host);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  /** Clipped horizontally — the state the ellipsis is rendered for. */
  private syncTruncation(): void {
    this.tooltip.disabled = this.host.scrollWidth <= this.host.clientWidth;
  }
}
